import { useClientApplicationsQuery } from "@graphql/client-applications/ClientApplications.g";
import { useClientEmployeesQuery } from "@graphql/client-employees/clientEmployees.g";
import { forwardRef, useImperativeHandle, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { DateRangePicker } from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { Select } from "@design-system/Select/Select";
import { TextField } from "@design-system/TextField/TextField";

export type ClientEmployeeFileUploadsFilterFormValues = {
  fileNameSearch?: string;
  mimetypeSearch?: string;
  datetime_Gte?: string;
  datetime_Lte?: string;
  employeeId?: string;
  applicationId?: string;
};

export type ClientEmployeeFileUploadsFilterFormRef = {
  getCurrentValues: () => ClientEmployeeFileUploadsFilterFormValues;
};

type Props = {
  values: ClientEmployeeFileUploadsFilterFormValues;
  onFilterChange: SubmitHandler<ClientEmployeeFileUploadsFilterFormValues>;
  errorsAPI?: unknown;
  clientId?: string;
  hideEmployeeFilter?: boolean;
  hideApplicationFilter?: boolean;
};

export const ClientEmployeeFileUploadsTableFilters = forwardRef<
  ClientEmployeeFileUploadsFilterFormRef,
  Props
>(
  (
    {
      values,
      onFilterChange,
      errorsAPI,
      clientId,
      hideEmployeeFilter,
      hideApplicationFilter,
    },
    ref,
  ) => {
    const { handleSubmit, setValue, watch, getValues } =
      useForm<ClientEmployeeFileUploadsFilterFormValues>({
        values: {
          fileNameSearch: values.fileNameSearch ?? "",
          mimetypeSearch: values.mimetypeSearch ?? "",
          datetime_Gte: values.datetime_Gte ?? undefined,
          datetime_Lte: values.datetime_Lte ?? undefined,
          employeeId: values.employeeId ?? undefined,
          applicationId: values.applicationId ?? undefined,
        },
      });

    useImperativeHandle(ref, () => ({
      getCurrentValues: () => getValues(),
    }));

    const fileNameSearch = watch("fileNameSearch");
    const mimetypeSearch = watch("mimetypeSearch");
    const datetime_Gte = watch("datetime_Gte");
    const datetime_Lte = watch("datetime_Lte");
    const employeeId = watch("employeeId");
    const applicationId = watch("applicationId");

    const employeesQuery = useClientEmployeesQuery(
      {
        clientId,
        first: 100,
        hasFileUploads: !applicationId ? true : undefined,
        hasFileUploadsInApplication: applicationId || undefined,
      },
      { enabled: !hideEmployeeFilter && !!clientId },
    );
    
    const applicationsQuery = useClientApplicationsQuery(
      {
        clientId,
        first: 100,
        hasFileUploads: !employeeId ? true : undefined,
        hasFileUploadsByEmployee: employeeId || undefined,
      },
      { enabled: !hideApplicationFilter && !!clientId },
    );

    const employeeOptions = useMemo(() => {
      const edges = employeesQuery.data?.clientEmployees?.edges ?? [];
      return edges
        .map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => !!n)
        .map((emp) => ({ id: emp.id, label: emp.name || emp.email }));
    }, [employeesQuery.data]);

    const applicationOptions = useMemo(() => {
      const edges = applicationsQuery.data?.clientApplications?.edges ?? [];
      return edges
        .map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => !!n)
        .map((app) => ({
          id: app.id,
          label:
            app.resource?.productName ||
            app.resource?.vendorName ||
            "Unknown Application",
        }));
    }, [applicationsQuery.data]);

    return (
      <Form
        onSubmit={handleSubmit(onFilterChange)}
        errorsAPI={errorsAPI as Record<string, string> | undefined}
      >
        <FieldGroup label="File Name">
          <TextField
            placeholder="Search by file name..."
            value={fileNameSearch ?? ""}
            onChange={(e) =>
              setValue("fileNameSearch", e.target.value, { shouldDirty: true })
            }
          />
        </FieldGroup>

        <FieldGroup label="Mimetype">
          <TextField
            placeholder="Search by mimetype..."
            value={mimetypeSearch ?? ""}
            onChange={(e) =>
              setValue("mimetypeSearch", e.target.value, { shouldDirty: true })
            }
          />
        </FieldGroup>

        <FieldGroup label="Date Range">
          <DateRangePicker
            value={[
              datetime_Gte ? new Date(datetime_Gte) : null,
              datetime_Lte ? new Date(datetime_Lte) : null,
            ]}
            onChange={([start, end]) => {
              setValue("datetime_Gte", start?.toISOString(), {
                shouldDirty: true,
              });
              setValue("datetime_Lte", end?.toISOString(), {
                shouldDirty: true,
              });
            }}
            placeholder="Select date range"
          />
        </FieldGroup>

        {!hideEmployeeFilter && !!clientId && (
          <FieldGroup label="Employee">
            <Select
              options={employeeOptions}
              value={employeeId ?? ""}
              onChange={(value) =>
                setValue("employeeId", value as string | undefined, {
                  shouldDirty: true,
                })
              }
            />
          </FieldGroup>
        )}

        {!hideApplicationFilter && !!clientId && (
          <FieldGroup label="Application">
            <Select
              options={applicationOptions}
              value={applicationId ?? ""}
              onChange={(value) =>
                setValue("applicationId", value as string | undefined, {
                  shouldDirty: true,
                })
              }
            />
          </FieldGroup>
        )}

        {/* Hidden submit button to enable Enter key submission */}
        <Button type="submit" style={{ display: "none" }} />
      </Form>
    );
  },
);

ClientEmployeeFileUploadsTableFilters.displayName =
  "ClientEmployeeFileUploadsTableFilters";
